import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { openClose } from '@core/animations/animations';
import {
	Branch,
	FinancialInstitutionTypes,
	FinancialInstitutionLender,
	MortgageeType,
	ContactInfo,
	PopularLenders,
	PopularCSLenders,
} from '@models/lender';
import {
	BusinessMatterClientWithAssociations,
	MatterClientType,
	MatterFileType,
	PersonMatterClientWithAssociations,
} from '@models/matter';
import { Select } from '@ngxs/store';
import { InstitutionBranchRepo } from '@repos/institutionBranch/institution-branch.service';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { Action } from '@state/utils/models';
import { cloneDeep, isEqual } from 'lodash';
import LogRocket from 'logrocket';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { ICON } from '@core/util/icon';
import { SnackbarService } from '@services/ui/snackbar.service';
import { checkIfMajorLender } from '@core/util/mortgage';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
	CreateInstitutionBranchComponent,
	CreateOrUpdateInstitutionModel,
} from 'src/app/private/sale/encumbrances/encumbrance/components/institution/create-branch/create-branch.component';
import {
	CreateInstitutionComponent,
	CreateInstitutionData,
} from 'src/app/private/sale/encumbrances/encumbrance/components/institution/create-institution/create-institution.component';
import { OnSelectBranch } from '@components/searchable-fields/branch-search/branch-search.component';
import { InstitutionBadgeDetails } from '../../atomic/organisms/institution-badge/institution-badge.component';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { MatterClients } from '@state/global/matter_clients';
import {
	BusinessClient,
	ClientCreationData,
	PersonClient,
	Client,
	ClientType,
} from '@models/clients';
import {
	LenderClientAssociation,
	LenderClientAssociationTypes,
} from '@components/lender-card/client-lender-card/client-lender-card.component';
import { MatterClientDispatch } from '@state/matter_clients/matterClients.dispatch';

export enum dropdownType {
	EDIT = 'Edit',
	REMOVE = 'Remove',
	ADD_BRANCH = 'Add Branch',
	SET_AS_PRIMARY = 'Set as primary',
}

const dropDownItems: DropdownOption<dropdownType>[] = [
	{
		name: 'Edit',
		icon: ICON.EDIT,
		value: dropdownType.EDIT,
	},
	{
		name: 'Add branch',
		icon: ICON.ADD,
		value: dropdownType.ADD_BRANCH,
	},
	{
		name: 'Set as primary',
		icon: ICON.STAR,
		value: dropdownType.SET_AS_PRIMARY,
	},
	{
		name: 'Remove',
		icon: ICON.DELETE,
		classes: ['text-red'],
		value: dropdownType.REMOVE,
	},
];

@UntilDestroy()
@Component({
	selector: 'app-institution-card',
	templateUrl: './institution-card.component.html',
	styleUrls: ['./institution-card.component.scss'],
	animations: [openClose()],
	standalone: false,
})
export class InstitutionCardComponent implements OnInit {
	public financialInstitution: FinancialInstitutionLender;
	public financialInstitutionTypes = FinancialInstitutionTypes;
	public allowInstEdit = true;
	public clientType = ClientType;

	@Input() primary: boolean = false;
	@Input() mortgageeType: MortgageeType;
	@Input() set finInstitution(institution: FinancialInstitutionLender) {
		this.financialInstitution = cloneDeep(institution);
		this.setContactDetails(institution);
		this.setBranchContactDetails(institution?.branch);
		this.mortgageeType = institution?.mortgageeType;
		if (institution) {
			this.branchDropDownItems = this.branchDropDownItems.filter(
				(item) => item.value !== dropdownType.ADD_BRANCH,
			);
			if (this.showDischargeFields) {
				this.allowInstEdit = !['001', '002', '003'].includes(
					this.financialInstitution.institutionNumber,
				);

				if (!this.allowInstEdit) {
					this.institutionDropDownItems = this.institutionDropDownItems.filter(
						(item) => item.value !== dropdownType.EDIT,
					);
				}
			} else {
				this.institutionDropDownItems = this.branchDropDownItems;
			}
		}
	}

	@Input() showDischargeFields: boolean;
	@Input() pushChangesToGlobalData: boolean = true;
	@Input() hideKebab: boolean = false;
	@Input() hideBranchSelector: boolean = false;
	@Input() hideContactDetails: boolean = false;

	@SelectSnapshot(MatterClients.get_clientPrivateLendersWithAssociationMapped)
	privateLendersWithAssociations: (
		| PersonMatterClientWithAssociations
		| BusinessMatterClientWithAssociations
	)[];
	@SelectSnapshot(SelectedMatter.matterFileType) fileType: MatterFileType;
	@Select(MatterClients.get_investors) investorsWithAssociations$: Observable<
		(
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[]
	>;

	@Output() editBranch: EventEmitter<Branch> = new EventEmitter();
	@Output() removeBranch: EventEmitter<void> = new EventEmitter();
	@Output() setBranch: EventEmitter<Branch> = new EventEmitter();
	@Output() createBranch: EventEmitter<Branch> = new EventEmitter();

	@Output() addInstitution: EventEmitter<FinancialInstitutionLender> =
		new EventEmitter<FinancialInstitutionLender>();
	@Output() editInstitution: EventEmitter<FinancialInstitutionLender> =
		new EventEmitter();
	@Output() removeInstitution: EventEmitter<FinancialInstitutionLender> =
		new EventEmitter();
	@Output() createInstitution: EventEmitter<FinancialInstitutionLender> =
		new EventEmitter();
	@Output() onAddAssociation: EventEmitter<LenderClientAssociation> =
		new EventEmitter<LenderClientAssociation>();
	@Output() onRemoveInvestor: EventEmitter<string> = new EventEmitter<string>();
	@Output() onSetAsPrimary: EventEmitter<string> = new EventEmitter<string>();

	@Input() collapsed: boolean = false;
	@Output() collapsedChange: EventEmitter<boolean> =
		new EventEmitter<boolean>();
	@Input() collapsable: boolean = false;
	@Input() highlightOnExpand: boolean = false;

	public ICON = ICON;
	public branchDropDownItems = dropDownItems;
	public institutionDropDownItems = dropDownItems;
	public computerShareLenderDropdownItems = [
		dropDownItems.find((item) => item.value === dropdownType.EDIT),
	];
	public computerShareDropdownItems = dropDownItems.filter((item) =>
		[dropdownType.REMOVE, dropdownType.EDIT].includes(item.value),
	);
	public mortgageeTypes = MortgageeType;
	public lenders = PopularLenders;
	public popularCSLenders = PopularCSLenders;
	public fileTypes = MatterFileType;
	public institutionContactDetails: ContactInfo;
	public escalationContactDetails: ContactInfo;
	public branchContactDetails: ContactInfo;
	public branchEscalationContactDetails: ContactInfo;
	public showBranchEscalation = false;
	public showInstitutionEscalation = false;
	public showAddBranch = false;
	public associatedClientInvestors: {
		[key: string]: PersonClient | BusinessClient;
	} = {};
	public showClientSearch: boolean = false;

	constructor(
		private dialog: MatDialog,
		private branchRepo: InstitutionBranchRepo,
		private snackbar: SnackbarService,
		private MatterClientDispatch: MatterClientDispatch,
	) {}

	ngOnInit(): void {
		this.setContactDetails(this.financialInstitution);
		this.setBranchContactDetails(this.financialInstitution?.branch);
		this.investorsWithAssociations$
			.pipe(untilDestroyed(this))
			.subscribe((investorsWithAssociations) => {
				this.associatedClientInvestors = {};
				investorsWithAssociations.forEach((investor) => {
					this.associatedClientInvestors[investor.id] = investor;
				});
			});
	}

	_editBranch(): void {
		const data: CreateOrUpdateInstitutionModel = {
			showDischargeFields: this.showDischargeFields,
			branch: this.financialInstitution?.branch,
			institutionId: this.financialInstitution?.institutionNumber,
			showTransitNumber: this.mortgageeType === MortgageeType.INSTITUTION,
			branchLabel:
				this.mortgageeType === MortgageeType.COMPUTERSHARE
					? 'Lender'
					: 'Branch',
			institutionBadgeDetails:
				this.mortgageeType === MortgageeType.COMPUTERSHARE
					? {
							name: this.financialInstitution?.branch?.name,
							logoUrl: this.financialInstitution.branch.logoUrl,
						}
					: (cloneDeep(this.financialInstitution) as InstitutionBadgeDetails),
			fileType: this.fileType,
			mortgageeType: this.mortgageeType,
			create: false,
		};

		const _dialog = this.dialog.open(CreateInstitutionBranchComponent, {
			data,
			width: '80vw',
			maxWidth: '800px',
		});

		_dialog
			.afterClosed()
			.pipe(take(1))
			.subscribe((result: Branch) => {
				if (result) {
					if (!isEqual(this.financialInstitution?.branch, result)) {
						this.editBranch.emit({
							...this.financialInstitution?.branch,
							...result,
						});
						this.setBranchContactDetails(result);
						this.toggleSnackbar();
					}
				}
			});
	}

	onAddInvestor($event: Client) {
		this._addInvestor($event.id);
	}

	private _addInvestor(associatingClientId: string) {
		this.toggleClientSearch();
		this.onAddAssociation.emit({
			associationType: LenderClientAssociationTypes.INVESTOR,
			baseClientId: this.financialInstitution.id,
			associatingClientId: associatingClientId,
		});
	}

	onCreateInvestor(clientCreationData: ClientCreationData) {
		this.toggleClientSearch();
		this.MatterClientDispatch.CreateAndAddClientToMatter({
			client: clientCreationData.client,
			matterClientRelationship: {
				...clientCreationData.matterClientRelationship,
				clientTypes: [MatterClientType.INVESTOR],
			},
		});
		this._addInvestor(clientCreationData.client.id);
	}

	removeInvestor(investorId: string) {
		this.onRemoveInvestor.emit(investorId);
	}

	toggleCollapsed() {
		if (this.collapsable) {
			this.collapsed = !this.collapsed;
			this.collapsedChange.emit(this.collapsed);
		}
	}

	dropdownOptionSelect(ev: DropdownResult<DropdownOption<dropdownType>>): void {
		if (ev.result.value === dropdownType.REMOVE) {
			this.removeInstitution.emit();
		} else if (ev.result.value === dropdownType.EDIT) {
			if (this.mortgageeType === MortgageeType.COMPUTERSHARE) {
				this._editBranch();
			} else {
				this.updateInstitution();
			}
		} else if (ev.result.value === dropdownType.ADD_BRANCH) {
			this.showAddBranch = true;
		} else if (ev.result.value === dropdownType.SET_AS_PRIMARY) {
			this.onSetAsPrimary.emit(this.financialInstitution.id);
		}
	}

	branchDropdownOptionSelect(
		ev: DropdownResult<DropdownOption<dropdownType>>,
	): void {
		if (ev.result.value === dropdownType.REMOVE) {
			this._removeBranch();
		} else if (ev.result.value === dropdownType.EDIT) {
			this._editBranch();
		}
	}

	toggleAddBranch(): void {
		this.showAddBranch = !this.showAddBranch;
	}

	removeFinancialInstitution(): void {
		this.removeInstitution.emit();
	}

	updateInstitution(): void {
		const institutionModel: CreateInstitutionData = {
			showDischargeFields: this.showDischargeFields,
			fileType: this.fileType,
			institution: this.financialInstitution,
			create: false,
			majorBank: checkIfMajorLender(
				this.financialInstitution?.institutionNumber || '',
				this.mortgageeType,
			),
			type: this.mortgageeType,
			pushChangesToGlobalData: this.pushChangesToGlobalData,
		};
		const dialogRef = this.dialog.open(CreateInstitutionComponent, {
			data: institutionModel,
			width: '80vw',
			maxWidth: '800px',
		});

		dialogRef
			.afterClosed()
			.pipe(take(1), untilDestroyed(this))
			.subscribe((result: FinancialInstitutionLender) => {
				if (result) {
					if (!isEqual(this.financialInstitution, result)) {
						this.editInstitution.emit({
							...this.financialInstitution,
							...result,
						});
						this.setContactDetails(result);
						this.toggleSnackbar();
					}
				}
			});
	}

	async setLeadingCSLender(branchId: string): Promise<void> {
		try {
			const branchResponse = await this.branchRepo
				.Get(branchId, {
					financialInstitutionId: this.financialInstitution.institutionNumber,
				})
				.pipe(take(1))
				.toPromise();
			const branch = {
				...branchResponse.data(),
				id: branchResponse.id,
				logoUrl: branchResponse.data().logoUrl ?? null,
			} as Branch;
			this.setBranch.emit(branch);
			this.setBranchContactDetails(branch);
		} catch (err) {
			console.log(err);
			LogRocket.error('Error fetching institution with id', branchId, err);
		}
	}

	onSelectedBranch(selectedBranch: OnSelectBranch): void {
		this.setBranchContactDetails(selectedBranch.branch);

		if (selectedBranch.action === Action.SET)
			this.setBranch.emit(selectedBranch.branch);

		if (selectedBranch.action === Action.CREATE)
			this.createBranch.emit(selectedBranch.branch);
	}

	switchBranch(): void {
		this.showAddBranch = true;
		this._removeBranch();
	}

	_removeBranch(): void {
		if (this.mortgageeType === MortgageeType.COMPUTERSHARE) {
			this.removeInstitution.emit();
		} else {
			this.removeBranch.emit();
		}
	}

	setContactDetails(financialInstitution: FinancialInstitutionLender): void {
		if (financialInstitution) {
			const contactInfo: ContactInfo = {
				phone: financialInstitution.phone,
				fax: financialInstitution.fax,
				email: financialInstitution.email,
				phone2: financialInstitution.phone2,
				email2: financialInstitution.email2,
			};
			if (this.showDischargeFields) {
				this.institutionContactDetails =
					financialInstitution?.discharge?.primary || {};
				this.escalationContactDetails =
					financialInstitution?.discharge?.escalation || {};
				if (Object.values(this.escalationContactDetails).some((x) => x)) {
					this.showInstitutionEscalation = true;
				} else {
					this.showInstitutionEscalation = false;
				}
			} else {
				this.institutionContactDetails = contactInfo;
			}
		}
	}

	setBranchContactDetails(branch: Branch): void {
		if (branch) {
			const contactInfo: ContactInfo = {
				phone: branch.phone,
				fax: branch.fax,
				email: branch.email,
				phone2: branch.phone2,
				email2: branch.email2,
			};
			if (this.showDischargeFields) {
				this.branchContactDetails = branch.discharge?.primary || {};
				this.branchEscalationContactDetails =
					branch.discharge?.escalation || {};
				if (
					Object.values(this.branchEscalationContactDetails || {}).some(
						(value) => !!value,
					)
				) {
					this.showBranchEscalation = true;
				} else {
					this.showBranchEscalation = false;
				}
			} else {
				this.branchContactDetails = contactInfo;
			}
		}
	}

	toggleClientSearch(): void {
		this.showClientSearch = !this.showClientSearch;
	}

	toggleSnackbar(): void {
		if (this.pushChangesToGlobalData) {
			this.snackbar.openSnackBar({
				text: 'Lender details submitted for review.',
				type: 'success',
			});
		}
	}
}
